export interface BodyRef {
  id: number;
  parent?: BodyRef;
}

export interface CollisionRef {
  bodyA: BodyRef;
  bodyB: BodyRef;
  parentA?: BodyRef;
  parentB?: BodyRef;
}

function rootBody(body: BodyRef): BodyRef {
  let current = body;
  const seen = new Set<number>();
  while (current.parent && current.parent !== current && !seen.has(current.parent.id)) {
    seen.add(current.id);
    current = current.parent;
  }
  return current;
}

export function normalizeCollisionParents(collision: CollisionRef): [number, number] {
  const parentA = collision.parentA ?? rootBody(collision.bodyA);
  const parentB = collision.parentB ?? rootBody(collision.bodyB);
  return [parentA.id, parentB.id];
}

function pairKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function parsePair(key: string): [number, number] {
  const [a, b] = key.split(':').map(Number);
  return [a!, b!];
}

export class ContactGraph {
  private readonly counts = new Map<string, number>();

  begin(a: number, b: number): void {
    if (a === b) return;
    const key = pairKey(a, b);
    this.counts.set(key, (this.counts.get(key) ?? 0) + 1);
  }

  active(a: number, b: number): void {
    if (a !== b && !this.counts.has(pairKey(a, b))) this.begin(a, b);
  }

  end(a: number, b: number): void {
    if (a === b) return;
    const key = pairKey(a, b);
    const count = this.counts.get(key) ?? 0;
    if (count <= 1) this.counts.delete(key);
    else this.counts.set(key, count - 1);
  }

  removeBody(bodyId: number): void {
    for (const key of this.counts.keys()) {
      const [a, b] = parsePair(key);
      if (a === bodyId || b === bodyId) this.counts.delete(key);
    }
  }

  clear(): void {
    this.counts.clear();
  }

  hasContact(a: number, b: number): boolean {
    return a !== b && (this.counts.get(pairKey(a, b)) ?? 0) > 0;
  }

  connectedTo(rootId: number): Set<number> {
    return new Set(this.distancesFrom(rootId).keys());
  }

  distancesFrom(rootId: number, excludedIds: ReadonlySet<number> = new Set()): Map<number, number> {
    const adjacency = new Map<number, Set<number>>();
    for (const key of this.counts.keys()) {
      const [a, b] = parsePair(key);
      if (excludedIds.has(a) || excludedIds.has(b)) continue;
      if (!adjacency.has(a)) adjacency.set(a, new Set());
      if (!adjacency.has(b)) adjacency.set(b, new Set());
      adjacency.get(a)!.add(b);
      adjacency.get(b)!.add(a);
    }
    const distances = new Map<number, number>([[rootId, 0]]);
    const queue = [rootId];
    for (let index = 0; index < queue.length; index += 1) {
      const current = queue[index]!;
      for (const neighbor of adjacency.get(current) ?? []) {
        if (!distances.has(neighbor)) {
          distances.set(neighbor, distances.get(current)! + 1);
          queue.push(neighbor);
        }
      }
    }
    return distances;
  }
}
